import { FileResponseObject } from '@components/file-field/file-input';

import type { AttributeFieldType } from 'api/attributes/requests';
import { AIModel, AIReasoningEffort } from 'api/types';

import { request } from '../request';

export type VectorStoreStatus = 'pending' | 'indexing' | 'ready' | 'failed';

export type AIAgent = {
  createdByName: string;
  description: string;
  isActive: boolean;
  model: AIModel;
  name: string;
  picture?: string;
  files: string[];
  shortId: string;
  systemInstructions: string;
  temperature?: number;
  reasoningEffort?: AIReasoningEffort;
  topP?: number;
  output: {
    name: string;
    label: string;
    type: AttributeFieldType;
    instructions: string;
  }[];
  useWebSearch: boolean;
  vectorStoreStatus: VectorStoreStatus;
  useFileSearch: boolean;
  dateCreated: string;
  dateUpdated: string;
};

export const loadAIAgents = async (queryString: string = '') => {
  const response = await request.get<AIAgent[]>(`v1/ai-agents/${queryString}`);
  return response.data;
};

export const loadAIAgent = async (shortId: string) => {
  const response = await request.get<AIAgent>(`v1/ai-agents/${shortId}/`);
  return response.data;
};

export type HiddenModelsResponse = {
  models: string[];
};

export const loadHiddenModels = async () => {
  const response = await request.get<HiddenModelsResponse>('v1/ai-agents/hidden-models/');
  return response.data;
};

type EditAIAgentProps = Pick<
  AIAgent,
  | 'name'
  | 'description'
  | 'systemInstructions'
  | 'model'
  | 'reasoningEffort'
  | 'temperature'
  | 'output'
  | 'files'
  | 'picture'
  | 'useWebSearch'
  | 'useFileSearch'
> & {
  shortId?: string;
};

export const createAIAgent = async (data: EditAIAgentProps) => {
  const response = await request.post('v1/ai-agents/', data);
  return response.data;
};

export const updateAIAgent = async (data: EditAIAgentProps) => {
  const response = await request.patch(`v1/ai-agents/${data.shortId}/`, data);
  return response.data;
};

export type DuplicateAIAgentData = {
  name: string;
  includeFiles: boolean;
};

export const duplicateAIAgent = async (shortId: string, data: DuplicateAIAgentData) => {
  const response = await request.post(`v1/ai-agents/${shortId}/duplicate/`, data);
  return response.data;
};

export const removeAIAgent = async (shortId: string) => {
  const response = await request.delete(`v1/ai-agents/${shortId}/`);
  return response.data;
};

export type TestAIAgentFile = string | FileResponseObject;

type TestAIAgentData = {
  actionId: string;
  additionalFiles: Array<TestAIAgentFile>;
  websocketId: string;
  testValues: Record<string, unknown>;
};

export const testAIAgent = async (data: TestAIAgentData) => {
  const response = await request.post(`v1/ai-agents/test/`, data);
  return response.data;
};

export type GenerateAIAgentOutputFromInstructionsProps = {
  systemInstructions: string;
  websocketId: string;
};

export const generateAIAgentOutputFromInstructions = async (
  props: GenerateAIAgentOutputFromInstructionsProps,
) => {
  const response = await request.post(`v1/ai-agents/generate-output-from-instructions/`, {
    systemInstructions: props.systemInstructions,
    websocketId: props.websocketId,
  });
  return response.data;
};
